import { useState } from 'react';
import { Game } from '../types';
import FactorBars from './FactorBars';

interface Props {
  game: Game;
}

const PROB_COLOR = (p: number) =>
  p >= 20 ? '#15803D' : p >= 10 ? '#1535A0' : '#D42B2B';

const TAG_STYLE = {
  good:    { background: '#DCFCE7', color: '#15803D' },
  neutral: { background: '#F0F3FF', color: '#1535A0' },
  bad:     { background: '#FEF2F2', color: '#DC2626' },
};

export default function GameRow({ game }: Props) {
  const [expanded, setExpanded] = useState(false);
  const pc = PROB_COLOR(game.prob);

  return (
    <div style={{ borderBottom: '1px solid #EEF0F6' }}>

      {/* ── MAIN ROW ── */}
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          padding: '14px 20px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          cursor: 'pointer',
          background: expanded ? '#FAFBFF' : '#fff',
          transition: 'background 0.15s',
          borderLeft: game.strong
            ? `3px solid ${pc}`
            : '3px solid transparent',
        }}
      >
        {/* LEFT — game info */}
        <div style={{ flex: 1 }}>

          {/* teams + strong badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              fontSize: '0.95rem',
              fontWeight: 700,
              letterSpacing: 0.3,
              color: '#0A1740',
            }}>
              {game.awayAbbr} @ {game.homeAbbr}
            </span>
            {game.strong && (
              <span style={{
                fontSize: '0.55rem',
                fontWeight: 700,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                color: pc,
                background: `${pc}18`,
                padding: '2px 6px',
                borderRadius: 4,
              }}>
                Strong
              </span>
            )}
          </div>

          {/* venue + time */}
          <div style={{
            fontSize: '0.68rem',
            color: '#8090B0',
            marginTop: 2,
          }}>
            {game.venue} · {game.time}
          </div>

          {/* pitchers */}
          <div style={{
            display: 'flex',
            gap: 6,
            marginTop: 7,
            flexWrap: 'wrap',
          }}>
            <span style={{
              fontSize: '0.68rem',
              color: '#4A5680',
              background: '#F5F6FA',
              padding: '2px 7px',
              borderRadius: 5,
              fontWeight: 500,
            }}>
              {game.awayPitcher.split(' ').pop()} {game.awayHR9}
            </span>
            <span style={{
              fontSize: '0.68rem',
              color: '#4A5680',
              background: '#F5F6FA',
              padding: '2px 7px',
              borderRadius: 5,
              fontWeight: 500,
            }}>
              {game.homePitcher.split(' ').pop()} {game.homeHR9}
            </span>
          </div>

          {/* tags */}
          <div style={{
            display: 'flex',
            gap: 5,
            marginTop: 7,
            flexWrap: 'wrap',
          }}>
            {game.tags.map(t => (
              <span
                key={t.label}
                style={{
                  fontSize: '0.62rem',
                  fontWeight: 600,
                  padding: '2px 7px',
                  borderRadius: 6,
                  ...TAG_STYLE[t.type],
                }}
              >
                {t.label}
              </span>
            ))}
          </div>
        </div>

        {/* RIGHT — probability + chevron */}
        <div style={{ flexShrink: 0, textAlign: 'right' }}>
          <div style={{
            fontFamily: "'Bebas Neue', sans-serif",
            fontSize: '1.9rem',
            color: pc,
            lineHeight: 1,
          }}>
            {game.prob}%
          </div>
          <div style={{
            fontSize: '0.55rem',
            fontWeight: 700,
            color: '#8090B0',
            letterSpacing: 1,
            textTransform: 'uppercase',
            marginTop: 1,
          }}>
            no HR
          </div>
          <div style={{
            fontSize: '0.7rem',
            color: expanded ? '#1535A0' : '#CBD5E8',
            marginTop: 6,
            transition: 'color 0.15s',
          }}>
            {expanded ? '▲' : '▼'}
          </div>
        </div>
      </div>

      {/* ── EXPANDABLE FACTOR BARS ── */}
      <div style={{
        maxHeight: expanded ? 400 : 0,
        overflow: 'hidden',
        transition: 'max-height 0.35s cubic-bezier(0.4,0,0.2,1)',
      }}>
        <FactorBars factors={game.factors} />
      </div>

    </div>
  );
}
